// Per-deployment brand configuration (white-labelling).
//
// Defaults are Therapy by Carole. A per-clinic deployment overrides these by
// setting REACT_APP_BRAND_* environment variables at build time — a second
// Netlify site built from this same repo, no code fork. Later the same values
// can be driven at runtime from a clinic_settings row instead of env.
//
// applyBrandTheme() writes any colour overrides onto :root so both the solid
// (var(--navy)) and the translucent (rgba(var(--navy-rgb), a)) usages retheme.

const env = process.env;

export const BRAND = {
  name: env.REACT_APP_BRAND_NAME || 'Therapy by Carole',
  logo: env.REACT_APP_BRAND_LOGO || '/Logo.jpg',
  website: env.REACT_APP_BRAND_WEBSITE || 'therapybycarole.co.uk',
  email: env.REACT_APP_BRAND_EMAIL || 'info@therapybycarole.co.uk',
  // How the client is addressed about their therapist in copy: "Ask Carole a
  // question", "Send Carole a video". A deployment with several therapists can
  // set this to something generic like "your therapist" — every string it
  // appears in reads correctly either way, and none of them assume a gender.
  therapistName: env.REACT_APP_BRAND_THERAPIST_NAME || 'Carole',
};

// Clinic locations offered when recording a session or filing a client.
// Comma-separated in env, e.g. "The Health Rooms, Peebles|Project Physio, Edinburgh"
// is pipe-separated because the names themselves contain commas.
export const CLINICS = (env.REACT_APP_BRAND_CLINICS || [
  'The Health Rooms, Peebles',
  'Project Physio, Edinburgh',
  'Stuart Barton Physiotherapy, Anstruther',
].join('|')).split('|').map(s => s.trim()).filter(Boolean);

// The clinic's own name, without the town — what gets shown in pickers and
// chips. The stored value keeps the town so a session record still reads
// unambiguously in an export.
export function clinicName(location) {
  return (location || '').split(',')[0].trim();
}

// '#rrggbb' or '#rgb' -> 'r, g, b' (channels for rgba()), or null if invalid.
function hexToRgbChannels(hex) {
  if (!hex) return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

// CSS custom property -> overriding hex from env (falsy = keep the default).
const COLOUR_OVERRIDES = {
  '--navy': env.REACT_APP_BRAND_NAVY,
  '--terracotta': env.REACT_APP_BRAND_TERRACOTTA,
  '--cream': env.REACT_APP_BRAND_CREAM,
  '--peach': env.REACT_APP_BRAND_PEACH,
};

// Solid colours that also carry an -rgb channel token to keep in sync.
const RGB_CHANNEL = {
  '--navy': '--navy-rgb',
  '--terracotta': '--terracotta-rgb',
  '--cream': '--cream-rgb',
};

export function applyBrandTheme(root = document.documentElement) {
  Object.entries(COLOUR_OVERRIDES).forEach(([cssVar, hex]) => {
    if (!hex) return;
    root.style.setProperty(cssVar, hex);
    const rgbVar = RGB_CHANNEL[cssVar];
    const channels = hexToRgbChannels(hex);
    if (rgbVar && channels) root.style.setProperty(rgbVar, channels);
  });
  document.title = BRAND.name;
  // Keep the browser/PWA chrome colour in step with a re-themed navy.
  if (COLOUR_OVERRIDES['--navy']) {
    document.querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', COLOUR_OVERRIDES['--navy']);
  }
}
